import { KeyboardEvent, useEffect } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, TextField } from '@client/new/components/basic';
import { Input } from '@design-system/components/input/input';
import { Button } from '@design-system/components/button/button';
import * as styles from './ProgramForm.css';
import { AdvancedSection } from './sections/AdvancedSection';
import { Divider } from './primitives/Divider';
import { DurationSection } from './sections/DurationSection';
import { FaqsSection } from './sections/FaqsSection';
import { InAppPurchaseSection } from './sections/InAppPurchaseSection';
import { PresentationMediaSection } from './sections/PresentationMediaSection';
import { ProgramSwapsField } from './ProgramSwapsField';
import { ReviewsSection } from './sections/ReviewsSection';
import { StructureSection } from './sections/StructureSection';
import {
  programFormSchema,
  publishedProgramFormSchema,
} from '../model/programForm.schema';
import {
  PROGRAM_GOAL_OPTIONS,
  ProgramFormValues,
} from '../model/programForm.types';

type ProgramFormProps = {
  defaultValues: ProgramFormValues;
  isSubmitting?: boolean;
  submitLabel: string;
  // Both present = editing an existing program; enables the swaps toggle
  // (engine config is keyed by program id, so a not-yet-created program has
  // nothing to toggle).
  creatorId?: number;
  programId?: number;
  // Returning `true` signals a successful save and triggers a dirty-state
  // reset so a follow-up Esc/backdrop close does not prompt about discarded
  // changes. Anything else (void from tests, `false` from a failed upsert)
  // leaves the dirty state intact so unsaved edits stay protected.
  onSave: (values: ProgramFormValues) => void | Promise<boolean>;
  onDirtyChange?: (isDirty: boolean) => void;
  isPublished?: boolean;
};

const DESCRIPTION_ROWS = 5;

export const ProgramForm = ({
  defaultValues,
  isSubmitting,
  submitLabel,
  creatorId,
  programId,
  onSave,
  onDirtyChange,
  isPublished = false,
}: ProgramFormProps) => {
  const methods = useForm<ProgramFormValues>({
    resolver: zodResolver(
      isPublished
        ? publishedProgramFormSchema(defaultValues)
        : programFormSchema,
    ),
    defaultValues,
  });
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = methods;

  const contentStructure = useWatch({ control, name: 'contentStructure' });
  const isCategoryStructure = contentStructure === 'category';

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const submit = handleSubmit(async (values) => {
    // Only clear dirty state after the upsert resolves successfully.
    // Resetting synchronously would leave isDirty=false on a failed save and
    // let an Esc/backdrop close silently drop the user's edits.
    const result = await onSave(values);
    if (result === true) {
      reset(values, { keepValues: true });
    }
  });

  // Enter on a single-line field implicitly submits the form; QA hit this by
  // accident while typing. Submit only via the explicit button click.
  const preventImplicitSubmit = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (target instanceof HTMLTextAreaElement) return;
    event.preventDefault();
  };

  return (
    <FormProvider {...methods}>
      <form
        className={styles.form}
        onSubmit={submit}
        onKeyDown={preventImplicitSubmit}
        data-testid="program-form"
        noValidate
      >
        <div className={styles.headerRow}>
          <div className={styles.nameField}>
            <Input
              autoFocus
              placeholder="Name your program"
              description="This title will be shown to your users"
              aria-label="Name"
              testId="program-form-name"
              error={errors.name?.message}
              disabled={isSubmitting}
              {...register('name')}
            />
          </div>
          <div className={styles.submitButton}>
            <Button
              type="submit"
              variant="filled"
              color="neutral"
              loading={isSubmitting}
              disabled={isSubmitting}
              testId="program-form-submit"
            >
              {submitLabel}
            </Button>
          </div>
        </div>

        <div className={styles.goalField}>
          <Controller
            control={control}
            name="goal"
            render={({ field }) => (
              <Autocomplete
                freeSolo
                options={PROGRAM_GOAL_OPTIONS}
                value={field.value}
                onChange={(_, v) => field.onChange(v ?? '')}
                onInputChange={(_, v) => field.onChange(v)}
                disabled={isSubmitting}
                data-testid="program-form-goal"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Goal"
                    placeholder="Select or type goal"
                    error={!!errors.goal}
                    helperText={errors.goal?.message}
                  />
                )}
              />
            )}
          />
        </div>

        <TextField
          label="Description"
          multiline
          rows={DESCRIPTION_ROWS}
          disabled={isSubmitting}
          error={!!errors.description}
          helperText={errors.description?.message}
          inputProps={{ 'data-testid': 'program-form-description' }}
          sx={{ '& textarea': { resize: 'none' } }}
          {...register('description')}
        />

        <Divider />
        {creatorId !== undefined && programId !== undefined ? (
          <ProgramSwapsField
            creatorId={creatorId}
            programId={programId}
            isSubmitting={isSubmitting}
          />
        ) : null}
        <PresentationMediaSection isSubmitting={isSubmitting} />
        <Divider />
        <StructureSection isSubmitting={isSubmitting} />
        {!isCategoryStructure ? (
          <>
            <Divider />
            <DurationSection isSubmitting={isSubmitting} />
          </>
        ) : null}
        <Divider />
        <AdvancedSection isSubmitting={isSubmitting} />
        {!isCategoryStructure ? (
          <>
            <Divider />
            <InAppPurchaseSection isSubmitting={isSubmitting} />
          </>
        ) : null}
        <Divider />
        <FaqsSection isSubmitting={isSubmitting} />
        <Divider />
        <ReviewsSection isSubmitting={isSubmitting} />
      </form>
    </FormProvider>
  );
};
